/**
 * Date & time formatting.
 *
 * The prototype hardcoded strings like "13:07", "7月13日 日曜日 · 8:04 am" and
 * "昨日". These build the same shapes from real dates. Everything works in the
 * device's local timezone — a nap that starts at 23:50 and ends at 00:20
 * belongs to the night you were awake for, not to UTC.
 *
 * Per the design system: figures always render in the mono/tabular face, in
 * both languages, so numbers line up regardless of surrounding script.
 */

const JP_WEEKDAYS = ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'] as const;
const JP_WEEKDAYS_SHORT = ['日', '月', '火', '水', '木', '金', '土'] as const;

/** "13:07" — 24-hour, zero-padded, what the log and sheet use. */
export function formatTime(d: Date): string {
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "8:04 am" — used in the home header line, matching the prototype. */
export function formatClock12(d: Date): string {
  const h24 = d.getHours();
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${h24 < 12 ? 'am' : 'pm'}`;
}

/** "7月13日 日曜日 · 8:04 am" */
export function formatDateLine(d: Date): string {
  return `${d.getMonth() + 1}月${d.getDate()}日 ${JP_WEEKDAYS[d.getDay()]} · ${formatClock12(d)}`;
}

/** "木曜日" */
export function weekdayJa(d: Date): string {
  return JP_WEEKDAYS[d.getDay()];
}

/** "Thursday" */
export function weekdayEn(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'long' });
}

/** "7/10" — the growth log's compact date. */
export function formatMonthDay(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** "7月13日（日）" */
export function formatDateShortJa(d: Date): string {
  return `${d.getMonth() + 1}月${d.getDate()}日（${JP_WEEKDAYS_SHORT[d.getDay()]}）`;
}

/** Local-day key, "YYYY-MM-DD". Used as the prepDays document id. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

/**
 * Which day the prep checklist points at by default: today while the
 * morning check is still plausible (the bento is packed the morning of),
 * tomorrow from noon — packing ahead happens in the afternoon and evening.
 * Weekends count like any other day; pool bags and 習い事 are prep too.
 */
export function prepTargetDay(now: Date = new Date()): Date {
  return now.getHours() < 12 ? startOfDay(now) : startOfDay(addDays(now, 1));
}

export function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

/**
 * きょう / あした / きのう for days around today, else the English weekday —
 * the sub-line under a stepped-to date in the prep and log day banners.
 */
export function dayContextLabel(day: Date, today: Date): string {
  if (isSameDay(day, today)) return 'きょう · Today';
  if (isSameDay(day, addDays(today, 1))) return 'あした · Tomorrow';
  if (isSameDay(day, addDays(today, -1))) return 'きのう · Yesterday';
  return weekdayEn(day);
}

/**
 * Activity-feed timestamp: 今 for the last minute, then the clock time for
 * today, 昨日 for yesterday, and M/D beyond that.
 */
export function formatRelative(d: Date, now: Date = new Date()): string {
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 60_000 && diffMs > -60_000) return '今';
  if (isSameDay(d, now)) return formatTime(d);
  if (isSameDay(d, addDays(now, -1))) return '昨日';
  return formatMonthDay(d);
}

/** "1時間30分" / "45分" — sleep durations. */
export function formatDurationJa(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}分`;
  if (rem === 0) return `${h}時間`;
  return `${h}時間${rem}分`;
}

/** "5h55m" — the compact total on the log summary tile. */
export function formatDurationCompact(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  return h === 0 ? `${m}m` : `${h}h${String(m % 60).padStart(2, '0')}m`;
}

export function minutesBetween(from: Date, to: Date): number {
  return (to.getTime() - from.getTime()) / 60_000;
}

/** Parse an ISO "YYYY-MM-DD" as a local date (not UTC midnight). */
export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function toISODate(d: Date): string {
  return dayKey(d);
}

/**
 * Fractional age in months — the growth chart's x axis, so a measurement
 * taken mid-month lands between the gridlines instead of snapping to one.
 * Uses an average month length; precise enough for a percentile plot.
 */
export function ageInMonths(birthDate: string, at: Date): number {
  const born = parseISODate(birthDate);
  return (at.getTime() - born.getTime()) / (1000 * 60 * 60 * 24 * 30.4375);
}

/** "4か月" for babies, "3歳" once past a year — the subtitle under a child's name. */
export function ageLabel(birthDate: string, now: Date = new Date()): string {
  const born = parseISODate(birthDate);
  let months = (now.getFullYear() - born.getFullYear()) * 12 + (now.getMonth() - born.getMonth());
  if (now.getDate() < born.getDate()) months -= 1;
  if (months < 0) return '0か月';
  if (months < 24) return `${months}か月`;
  return `${Math.floor(months / 12)}歳`;
}

/** "4 months" / "3 years" — the English half of the same line. */
export function ageLabelEn(birthDate: string, now: Date = new Date()): string {
  const born = parseISODate(birthDate);
  let months = (now.getFullYear() - born.getFullYear()) * 12 + (now.getMonth() - born.getMonth());
  if (now.getDate() < born.getDate()) months -= 1;
  if (months < 0) months = 0;
  if (months < 24) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'}`;
}

/** おはよう / こんにちは / こんばんは, matching the home screen greeting. */
export function greetingJa(d: Date = new Date()): string {
  const h = d.getHours();
  if (h < 11) return 'おはよう';
  if (h < 18) return 'こんにちは';
  return 'こんばんは';
}

export function greetingEn(d: Date = new Date()): string {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** Turn "13:00" into "1:00 pm" for the handover line's English half. */
export function clockStringTo12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h)) return hhmm;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m ?? 0).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}
